import React from 'react';
import { specialisationName } from '../../constants/staff';
import { formatToken } from '../../utils/formatToken';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * DoctorQueueCard — Reusable per-doctor queue display card
 *
 * Designed for:
 *   • Public TV displays / LED boards
 *   • Queue Dashboard (internal)
 *   • Multi-doctor hospital displays (side by side)
 *
 * Security:
 *   ✔ Shows ONLY: Token numbers, doctor public profile
 *   ✗ Does NOT show: Patient name, phone, age, gender, symptoms, notes
 *
 * Props:
 *   doctor      — { display_name, first_name, last_name, profile_picture_url,
 *                    qualification, specialization, experience_years, about,
 *                    consultation_department, availability, member_id }
 *   currentToken — Current serving token number (null if idle)
 *   currentAppointmentNumber — Appointment number for current serving (null if none)
 *   currentVisitType — 'WALK_IN' | 'APPOINTMENT' for current serving (optional)
 *   nextTokens   — Array of next 5 waiting: { token, appointmentNumber, visitType, isPriority, priorityType }
 *   waitingCount — Total number of patients waiting
 *   isDark       — Dark theme flag
 *   compact      — If true, show minimal view (for side-by-side hospital display)
 * ═══════════════════════════════════════════════════════════════════════
 */

const AVAIL_CONFIG = {
  available:    { label: 'Available',        icon: '🟢', color: '#22c55e', bg: 'rgba(34,197,94,0.1)',  border: 'rgba(34,197,94,0.25)' },
  on_hold:      { label: 'On Hold',          icon: '🟡', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.25)' },
  closed_today: { label: 'Closed for Today', icon: '🔴', color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  border: 'rgba(239,68,68,0.25)' },
};

export default React.memo(function DoctorQueueCard({
  doctor,
  currentToken,
  currentAppointmentNumber = null,
  currentVisitType = null,
  nextTokens = [],
  waitingCount = 0,
  isDark = false,
  compact = false,
}) {
  const avail = AVAIL_CONFIG[doctor.availability] || AVAIL_CONFIG.available;
  const isClosed = doctor.availability === 'closed_today';
  const isHold = doctor.availability === 'on_hold';
  const doctorName = doctor.display_name || `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim() || 'Doctor';

  // Card border color based on status
  const borderColor = isClosed
    ? avail.border
    : isHold
      ? avail.border
      : currentToken != null
        ? 'rgba(13, 148, 136,0.25)' // purple — consulting
        : waitingCount > 0
          ? 'rgba(59,130,246,0.2)' // blue — has queue
          : 'rgba(34,197,94,0.2)'; // green — idle

  return (
    <div style={{
      borderRadius: '1rem',
      border: `1px solid ${borderColor}`,
      background: isDark
        ? 'linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))'
        : '#fff',
      overflow: 'hidden',
      transition: 'border-color 0.2s',
    }}>
      {/* ── Doctor Header with Profile ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: compact ? '0.625rem 0.75rem' : '0.875rem 1rem',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
      }}>
        {/* Profile Picture */}
        <div style={{
          width: compact ? '2.5rem' : '3.25rem',
          height: compact ? '2.5rem' : '3.25rem',
          borderRadius: '50%',
          background: isDark
            ? 'linear-gradient(135deg, #115e59, #0f766e)'
            : 'linear-gradient(135deg, #0d9488, #0d9488)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
          border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
        }}>
          {doctor.profile_picture_url ? (
            <img
              src={doctor.profile_picture_url}
              alt={doctorName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <span style={{
            display: doctor.profile_picture_url ? 'none' : 'flex',
            fontSize: compact ? '1rem' : '1.25rem',
            color: '#fff',
            fontWeight: 700,
          }}>
            {(doctor.first_name?.[0] || 'D').toUpperCase()}
          </span>
        </div>

        {/* Doctor Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'space-between' }}>
            <h3 style={{
              margin: 0, fontSize: compact ? '0.8125rem' : '0.9375rem', fontWeight: 700,
              color: isDark ? '#f5f5f7' : '#111827',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              Dr. {doctorName}
            </h3>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.1875rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.5625rem',
              fontWeight: 600,
              color: avail.color,
              background: isDark ? `${avail.color}18` : avail.bg,
              border: `1px solid ${isDark ? `${avail.color}25` : avail.border}`,
              flexShrink: 0,
            }}>
              {avail.icon} {avail.label}
            </span>
          </div>

          {/* Qualification + Department */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap', marginTop: '0.125rem' }}>
            {doctor.qualification && (
              <span style={{
                fontSize: '0.625rem', fontWeight: 500,
                color: isDark ? '#5eead4' : '#0d9488',
                background: isDark ? 'rgba(13, 148, 136,0.12)' : 'rgba(13, 148, 136,0.08)',
                padding: '0.0625rem 0.375rem',
                borderRadius: '0.25rem',
              }}>
                {doctor.qualification}
              </span>
            )}
            {doctor.consultation_department && (
              <span style={{
                fontSize: '0.625rem', fontWeight: 500,
                color: isDark ? '#93c5fd' : '#3b82f6',
              }}>
                {doctor.consultation_department}
              </span>
            )}
            {doctor.specialization && !doctor.consultation_department && (
              <span style={{
                fontSize: '0.625rem', fontWeight: 500,
                color: isDark ? '#93c5fd' : '#3b82f6',
              }}>
                {/* FE-35 — the doctor's speciality NAME, never the stored
                    value: `MS_ENT` read as `MS_ENT` on this card. */}
                {specialisationName(doctor.specialization) || doctor.specialization}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── About Doctor (optional, non-compact only) ── */}
      {!compact && doctor.about && (
        <div style={{
          padding: '0.375rem 1rem',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}`,
        }}>
          <p style={{
            margin: 0, fontSize: '0.625rem',
            color: isDark ? '#9ca3af' : '#6b7280',
            lineHeight: 1.4,
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {doctor.about}
          </p>
        </div>
      )}

      {/* ── Closed State ── */}
      {isClosed && (
        <div style={{
          padding: '1.25rem 1rem',
          textAlign: 'center',
          color: isDark ? '#6b7280' : '#9ca3af',
          fontSize: '0.75rem',
        }}>
          <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.375rem' }}>🚫</span>
          Today's slots are full.
          {waitingCount > 0 && (
            <p style={{ margin: '0.375rem 0 0', fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
              {waitingCount} patient{waitingCount !== 1 ? 's' : ''} still waiting
            </p>
          )}
        </div>
      )}

      {/* ── Active Queue Body ── */}
      {!isClosed && (
        <div style={{ padding: compact ? '0.5rem 0.75rem' : '0.75rem 1rem' }}>
          {/* Current Token — Now Serving */}
          {currentToken != null ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.625rem',
              padding: compact ? '0.5rem 0.625rem' : '0.625rem 0.75rem',
              borderRadius: '0.75rem',
              background: isDark ? 'rgba(13, 148, 136,0.1)' : 'rgba(13, 148, 136,0.06)',
              border: `1px solid ${isDark ? 'rgba(13, 148, 136,0.2)' : 'rgba(13, 148, 136,0.12)'}`,
              marginBottom: '0.625rem',
            }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: compact ? '2rem' : '2.5rem',
                height: compact ? '2rem' : '2.5rem',
                borderRadius: '0.5rem',
                background: 'linear-gradient(135deg, #0f766e, #0d9488)',
                fontSize: compact ? '0.875rem' : '1.125rem',
                fontWeight: 800,
                color: '#fff',
                boxShadow: '0 2px 8px rgba(15, 118, 110,0.3)',
              }}>
                {formatToken(currentToken, currentAppointmentNumber, { hash: false })}
              </span>
              <div style={{ flex: 1 }}>
                <p style={{
                  margin: 0, fontSize: '0.5625rem', fontWeight: 700,
                  color: isDark ? '#5eead4' : '#0f766e',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>
                  Now Serving
                </p>
                <p style={{
                  margin: '0.125rem 0 0', fontSize: compact ? '0.75rem' : '0.875rem', fontWeight: 700,
                  color: isDark ? '#f5f5f7' : '#111827',
                }}>
                  Token {formatToken(currentToken, currentAppointmentNumber)}
                </p>
                {currentVisitType === 'APPOINTMENT' || currentAppointmentNumber ? (
                  <span style={{
                    fontSize: '0.5rem', padding: '0.0625rem 0.25rem',
                    borderRadius: '0.1875rem',
                    background: isDark ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.1)',
                    color: '#a855f7', fontWeight: 600,
                  }}>
                    📅 Appointment
                  </span>
                ) : currentVisitType === 'WALK_IN' ? (
                  <span style={{
                    fontSize: '0.5rem', padding: '0.0625rem 0.25rem',
                    borderRadius: '0.1875rem',
                    background: isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.08)',
                    color: '#3b82f6', fontWeight: 600,
                  }}>
                    Walk-in
                  </span>
                ) : null}
              </div>
              <span style={{
                fontSize: '0.5rem',
                padding: '0.125rem 0.375rem',
                borderRadius: '0.25rem',
                background: isDark ? 'rgba(13, 148, 136,0.15)' : 'rgba(13, 148, 136,0.08)',
                color: '#0d9488',
                fontWeight: 600,
                animation: 'pulse 2s infinite',
              }}>
                ● LIVE
              </span>
            </div>
          ) : (
            <div style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '0.625rem',
              background: isDark ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)',
              border: `1px solid ${isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)'}`,
              marginBottom: '0.625rem',
              textAlign: 'center',
              fontSize: '0.75rem',
              color: isDark ? '#4ade80' : '#16a34a',
              fontWeight: 500,
            }}>
              {isHold ? '⏸ Please expect some waiting time.' : waitingCount > 0 ? '✋ Ready for next patient' : '✅ No patients waiting'}
            </div>
          )}

          {/* Next Tokens */}
          {nextTokens.length > 0 && (
            <div style={{ marginBottom: '0.5rem' }}>
              <p style={{
                margin: '0 0 0.375rem', fontSize: '0.5625rem', fontWeight: 700,
                color: isDark ? '#60a5fa' : '#2563eb',
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                Next Up
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3125rem' }}>
                {nextTokens.map((item, idx) => {
                  const token = typeof item === 'object' ? item.token : item;
                  const apptNum = typeof item === 'object' ? item.appointmentNumber : null;
                  const visitType = typeof item === 'object' ? item.visitType : null;
                  const priorityType = typeof item === 'object' ? item.priorityType : null;

                  // Color scheme:  Appointment = purple, Requeued = amber, Walk-in = blue/gray
                  const isAppt = visitType === 'APPOINTMENT' || apptNum;
                  const isRequeued = priorityType === 'REQUEUED';
                  const tokenColor = isAppt
                    ? { bg: isDark ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.1)', border: isDark ? 'rgba(168,85,247,0.3)' : 'rgba(168,85,247,0.2)', text: '#a855f7' }
                    : isRequeued
                      ? { bg: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)', border: isDark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.2)', text: '#f59e0b' }
                      : idx === 0
                        ? { bg: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)', border: isDark ? 'rgba(59,130,246,0.25)' : 'rgba(59,130,246,0.15)', text: '#3b82f6' }
                        : { bg: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', text: isDark ? '#9ca3af' : '#6b7280' };

                  return (
                  <span
                    key={`${token}-${idx}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      gap: '0.1875rem',
                      minWidth: '2rem',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.6875rem',
                      fontWeight: idx === 0 ? 700 : 500,
                      background: tokenColor.bg,
                      color: tokenColor.text,
                      border: `1px solid ${tokenColor.border}`,
                    }}
                  >
                    {isAppt && <span style={{ fontSize: '0.5rem' }}>📅</span>}
                    {isRequeued && <span style={{ fontSize: '0.5rem' }}>↩</span>}
                    {formatToken(token, apptNum)}
                  </span>
                  );
                })}
              </div>
              {/* Legend — visible to patients */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap',
                marginTop: '0.375rem',
                padding: '0.25rem 0',
              }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.1875rem', fontSize: '0.5rem', color: isDark ? '#9ca3af' : '#9ca3af' }}>
                  <span style={{ width: '0.375rem', height: '0.375rem', borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />
                  Walk-in
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.1875rem', fontSize: '0.5rem', color: isDark ? '#9ca3af' : '#9ca3af' }}>
                  <span style={{ width: '0.375rem', height: '0.375rem', borderRadius: '50%', background: '#a855f7', display: 'inline-block' }} />
                  Appointment
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.1875rem', fontSize: '0.5rem', color: isDark ? '#9ca3af' : '#9ca3af' }}>
                  <span style={{ width: '0.375rem', height: '0.375rem', borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />
                  Returning
                </span>
              </div>
            </div>
          )}

          {/* Waiting Count */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.375rem 0.625rem',
            borderRadius: '0.5rem',
            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          }}>
            <span style={{
              fontSize: '0.625rem', fontWeight: 600,
              color: isDark ? '#6b7280' : '#9ca3af',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              Waiting
            </span>
            <span style={{
              fontSize: '1rem', fontWeight: 800,
              color: waitingCount > 0 ? (isDark ? '#60a5fa' : '#3b82f6') : (isDark ? '#4b5563' : '#d1d5db'),
            }}>
              {waitingCount}
            </span>
          </div>

          {/* Empty state */}
          {waitingCount === 0 && currentToken == null && !isHold && (
            <p style={{
              margin: '0.375rem 0 0', textAlign: 'center',
              fontSize: '0.625rem', color: isDark ? '#4b5563' : '#d1d5db',
            }}>
              Queue clear — no patients waiting
            </p>
          )}
        </div>
      )}
    </div>
  );
});
